import { useState, type ReactElement } from 'react';
import { gleicherOrdner, imOrdner, ordnerName } from '@schnitt/shared';
import { Sheet } from './Sheet';
import { OrdnerWahl } from './OrdnerWahl';

/**
 * Einen Ordner umbenennen, zusammenlegen oder aufloesen (7.20).
 *
 * Drei Handlungen, ein Blatt -- weil es aus Sicht der Daten dieselbe ist: das
 * Wort an jedem Projekt aendert sich. Auf einen vorhandenen Namen umbenennen
 * legt zusammen; das steht auch da, bevor man drueckt, denn zusammengelegt
 * ist zusammengelegt.
 */
export function OrdnerSheet({
  von,
  vorhandene,
  laeuft,
  onClose,
  onFertig,
}: {
  von: string;
  vorhandene: string[];
  laeuft: boolean;
  onClose: () => void;
  onFertig: (nach: string) => void | Promise<void>;
}): ReactElement {
  const [nach, setNach] = useState(von);
  const sauber = ordnerName(nach);
  const legtZusammen = vorhandene.some((f) => gleicherOrdner(f, sauber));
  const loest = sauber === '';
  const unveraendert = sauber === ordnerName(von);
  // In sich selbst geht nicht (S47): „Kunde“ nach „Kunde / Alt“.
  const inSich = !unveraendert && imOrdner(sauber, von);
  const ziele = vorhandene.filter((f) => !imOrdner(f, von));
  const hatUnterordner = vorhandene.some((f) => f !== von && imOrdner(f, von));

  return (
    <Sheet title={`Ordner „${von}“`} onClose={onClose}>
      <label className="field">
        <span>Neuer Name</span>
        <input
          value={nach}
          autoFocus
          maxLength={80}
          data-ordner-neuer-name
          onChange={(e) => setNach(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !unveraendert && !inSich && !laeuft) void onFertig(sauber);
          }}
        />
      </label>

      {ziele.length ? (
        <>
          <h3 style={{ fontSize: 13, color: 'var(--muted)', margin: '18px 0 8px' }}>Oder in einen anderen legen</h3>
          <OrdnerWahl wert={sauber} vorhandene={ziele} onWahl={setNach} />
        </>
      ) : null}

      {inSich ? (
        <p className="hint" data-ordner-warnung>
          „{von}“ kann nicht in sich selbst liegen.
        </p>
      ) : legtZusammen ? (
        <p className="hint" data-ordner-warnung>
          Wird mit „{vorhandene.find((f) => gleicherOrdner(f, sauber))}“ zusammengelegt.
        </p>
      ) : loest ? (
        <p className="hint" data-ordner-warnung>
          Der Ordner verschwindet. Die Projekte bleiben{hatUnterordner ? ', seine Unterordner rücken eine Ebene hoch' : ''}.
        </p>
      ) : null}

      <button
        className="primary block"
        style={{ marginTop: 14 }}
        disabled={laeuft || unveraendert || inSich}
        data-ordner-speichern
        onClick={() => void onFertig(sauber)}
      >
        {loest ? 'Ordner auflösen' : legtZusammen ? 'Zusammenlegen' : 'Umbenennen'}
      </button>
    </Sheet>
  );
}
